import { Fragment, useMemo } from 'react';
import { ReactComponent as HoofySvg } from './assets/hoofy.svg';

function randomInRange(min, max) {
  return min + Math.random() * (max - min);
}

function randomInstanceId() {
  return `hoofy-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * SVG filters live here (sibling to the cow), not inside hoofy.svg: applying
 * filter:url(#id) to the same <svg> that defines the filter makes WebKit paint
 * nothing. See HoofyCow output + `.hoofy-cow.email-gate__cow` in App.css.
 */
function HoofyHandDrawFilterDefs() {
  const seeds = [3, 19, 37, 53, 71, 89, 107, 131];
  return (
    <svg
      className="hoofy-hand-draw-filters"
      aria-hidden="true"
      focusable="false"
      width="0"
      height="0"
    >
      <defs>
        {seeds.map((seed, i) => (
          <filter
            key={seed}
            id={`hoofyHand${i}`}
            x="-18%"
            y="-18%"
            width="136%"
            height="136%"
            filterUnits="objectBoundingBox"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.032"
              numOctaves="2"
              seed={seed}
              result={`hoofyN${i}`}
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2={`hoofyN${i}`}
              scale="2.6"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        ))}
      </defs>
    </svg>
  );
}

/** Inline cow SVG so #C33541 heart paths can be styled (see `.hoofy-heart` in App.css). */
export function HoofyCow({ className, style, ...rest }) {
  const instanceId = useMemo(randomInstanceId, []);

  const organic = useMemo(
    () => ({
      h1Dur: randomInRange(3.65, 5.35),
      h1Delay: randomInRange(0, 0.55),
      h2Dur: randomInRange(3.85, 5.55),
      h2Delay: randomInRange(0.45, 1.5),
    }),
    [],
  );

  const h1Steps = Math.max(1, Math.round(organic.h1Dur * 12));
  const h2Steps = Math.max(1, Math.round(organic.h2Dur * 12));

  const mergedStyle = {
    ...style,
    '--hoofy-h1-dur': `${organic.h1Dur}s`,
    '--hoofy-h1-delay': `${organic.h1Delay}s`,
    '--hoofy-h2-dur': `${organic.h2Dur}s`,
    '--hoofy-h2-delay': `${organic.h2Delay}s`,
  };

  const heartStepCss = `
svg.hoofy-cow[data-hoofy-instance="${instanceId}"] .hoofy-heart:not(.hoofy-heart--delayed) {
  animation-timing-function: steps(${h1Steps}, end), steps(${h1Steps}, end);
}
svg.hoofy-cow[data-hoofy-instance="${instanceId}"] .hoofy-heart--delayed {
  animation-timing-function: steps(${h2Steps}, end), steps(${h2Steps}, end);
}
`.trim();

  return (
    <Fragment>
      <style>{heartStepCss}</style>
      <HoofyHandDrawFilterDefs />
      <HoofySvg
        className={['hoofy-cow', className].filter(Boolean).join(' ')}
        data-hoofy-instance={instanceId}
        style={mergedStyle}
        width={163}
        height={117}
        focusable="false"
        {...rest}
      />
    </Fragment>
  );
}
